import React, { useState } from 'react';
import { Button } from '../ui/button';
import { useCreateTrialSubscription } from '../../services/subscription-service';
import type { CreateTrialSubscriptionRequest } from '../../types/subscription';

interface TrialConfirmationProps {
  formData: CreateTrialSubscriptionRequest;
  onBack: () => void;
}

export function TrialConfirmation({ formData, onBack }: TrialConfirmationProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message?: string } | null>(null);
  
  const createTrialMutation = useCreateTrialSubscription();

  const handleCreateTrial = async () => {
    setIsCreating(true);
    
    try {
      const response = await createTrialMutation.mutateAsync(formData);
      
      if (response.success) {
        setResult({ 
          success: true, 
          message: `Trial created successfully! Your 60-day trial ends on ${new Date(response.trialEndDate!).toLocaleDateString()}.`
        });
      } else {
        setResult({ 
          success: false, 
          message: response.errorMessage || 'Failed to create trial subscription'
        });
      }
    } catch (error: any) {
      setResult({ 
        success: false, 
        message: error.message || 'An unexpected error occurred'
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (result) {
    return (
      <div className="text-center space-y-6">
        {result.success ? (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Welcome to CueFlow!
              </h3>
              <p className="text-gray-600 mb-4">
                {result.message}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  What's Next?
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Check your email for setup instructions</li>
                  <li>• Download the CueFlow instructor app</li>
                  <li>• Start tracking your first session</li>
                  <li>• We'll remind you before your trial ends</li>
                </ul>
              </div>
              <div className="space-x-4">
                <Button onClick={() => window.location.href = '/dashboard'}>
                  Go to Dashboard
                </Button>
                <Button variant="outline" onClick={() => window.location.href = '/download'}>
                  Download App
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Setup Failed
              </h3>
              <p className="text-gray-600 mb-6">
                {result.message}
              </p>
              <div className="space-x-4">
                <Button onClick={handleCreateTrial}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={onBack}>
                  Go Back
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Confirm Your Trial Setup
        </h3>
        <p className="text-gray-600">
          Review your information and start your 60-day free trial
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
        <div>
          <dt className="text-sm font-medium text-gray-500">Studio Name</dt>
          <dd className="text-sm text-gray-900">{formData.studioName}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Contact Email</dt>
          <dd className="text-sm text-gray-900">{formData.contactEmail}</dd>
        </div>
        {formData.phone && (
          <div>
            <dt className="text-sm font-medium text-gray-500">Phone</dt>
            <dd className="text-sm text-gray-900">{formData.phone}</dd>
          </div>
        )}
        {formData.address && (
          <div>
            <dt className="text-sm font-medium text-gray-500">Address</dt>
            <dd className="text-sm text-gray-900">{formData.address}</dd>
          </div>
        )}
        <div>
          <dt className="text-sm font-medium text-gray-500">MindBody Site ID</dt>
          <dd className="text-sm text-gray-900">{formData.mindBodySiteId}</dd>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              60-Day Free Trial
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <ul className="space-y-1">
                <li>• Full access to all features</li>
                <li>• No payment required now</li>
                <li>• Cancel anytime during trial</li>
                <li>• We'll email you before trial ends</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center text-xs text-gray-500">
        By starting your trial, you agree to our{' '}
        <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>{' '}
        and{' '}
        <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} disabled={isCreating}>
          Back
        </Button>
        <Button 
          onClick={handleCreateTrial}
          disabled={isCreating}
          className="w-full sm:w-auto"
        >
          {isCreating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating Trial...
            </>
          ) : (
            'Start Free Trial'
          )}
        </Button>
      </div>
    </div>
  );
}